<template>
	<Dialog
		:visible="f.visible.value"
		modal
		:header="tituloModal"
		:close-on-escape="false"
		:pt="{
			root: { style: 'width: 100%; max-width: 480px;' },
			closeButton: { style: 'display: none' },
		}"
		@show="focarInputPrincipal"
		@update:visible="(v) => { if (!v) f.cancelar() }"
	>
		<!-- ── Modo DUN14 / EAN13 ──────────────────────────────────────────── -->
		<R3FieldSet v-if="!f.isPesoVariavel.value" title="Informe o código de barras da embalagem">
			<R3Input
				:ref="f.inputDun14Ref"
				v-model:input-value="f.codigoBipado.value"
				input-i-d="inputDun14AuditoriaDun14"
				mode="text"
				class="flex w-full"
				:disabled-input="f.isLoading.value"
				@keydown:enter="onBipar"
			/>
		</R3FieldSet>

		<!-- ── Modo Peso Variável ──────────────────────────────────────────── -->
		<template v-else>
			<R3FieldSet title="Código de Barras (Balança ou EAN)">
				<R3Input
					:ref="f.inputDun14Ref"
					v-model:input-value="f.codigoBipado.value"
					input-i-d="inputCodBarPesoVar"
					mode="text"
					class="flex w-full"
					:disabled-input="f.isLoading.value"
					@input="onCodigoBarrasInput"
					@keydown:enter="onBiparPesoVariavel"
				/>
			</R3FieldSet>

			<R3FieldSet title="Peso do Volume (kg)">
				<R3Input
					:ref="f.inputPesoRef"
					v-model:input-value="pesoTexto"
					input-i-d="inputPesoAuditoriaDun14"
					mode="decimal"
					class="flex w-full"
					:disabled-input="f.isLoading.value || pesoDecodificadoAutomaticamente"
					@keydown:enter="onValidarPeso"
				/>
			</R3FieldSet>

			<div v-if="f.faixaTolerancia.value" class="flex justify-content-between mt-1 text-sm">
				<span>Faixa tolerada:</span>
				<span>
					<b>{{ f.faixaTolerancia.value.minima }}</b>
					–
					<b>{{ f.faixaTolerancia.value.maxima }}</b>
					kg
				</span>
			</div>

			<div v-if="pesoDecodificadoAutomaticamente" class="mt-1 text-sm" style="color: var(--green-600)">
				✓ Peso decodificado automaticamente pelo EAN de balança
			</div>
		</template>

		<!-- ── Botão Cancelar ──────────────────────────────────────────────── -->
		<div class="flex justify-content-end mt-3">
			<Button
				severity="danger"
				label="Cancelar"
				:disabled="f.isLoading.value"
				@click="f.cancelar()"
			/>
		</div>

		<!-- ── Modal de erro interno (padrão msgDlg) ──────────────────────── -->
		<ModalMessageDialog
			:display="f.erroDialogVisible.value"
			:msg="f.erroMensagem.value"
			:msg-properties="{ color: 'red' }"
			@ok-button-click="onFecharErroDialog"
		/>

		<!-- ── Modal de dupla confirmação de peso (Regra 7) ───────────────── -->
		<ModalMessageDialog
			:display="f.confirmacaoDialogVisible.value"
			:msg="`Confirma o peso do volume: <b>${f.pesoInformado.value} kg</b>?`"
			@ok-button-click="f.confirmarPeso()"
		/>
	</Dialog>
</template>

<script lang="ts" setup>
	import { computed, nextTick, onMounted, onUnmounted, watch } from "vue";
	import { Howl } from "howler";
	import { useAuditoriaVolumeDun14 } from "~/composables/useAuditoriaVolumeDun14";

	/**
	 * Obtém a instância injetada de `app.vue` (via AUDITORIA_VOLUME_DUN14_KEY).
	 * O contexto (empresaId, mercadoriaId, isPesoVariavel) é lido diretamente
	 * dos campos reativos da instância, definidos no momento em que `abrir()` é chamado.
	 */
	const { instancia: f, validarDun14, validarPesoVariavel } = useAuditoriaVolumeDun14();

	// ── Título dinâmico do modal ─────────────────────────────────────────────
	const tituloModal = computed(() =>
		f.isPesoVariavel.value ? "Confirmação de Peso" : "Confirmação DUN14",
	);

	// ── Áudio de erro — instância criada e destruída com o componente ────────
	/**
	 * O Howl é criado aqui (único local correto) e liberado em `onUnmounted`.
	 * O composable não toca em Howl — apenas sinaliza via `f.deveTocarAudio`.
	 */
	let audioErro: Howl | null = null;

	onMounted(() => {
		audioErro = new Howl({ src: ["/sounds/MSG_ERRO_SONORO.WAV"] });
	});

	onUnmounted(() => {
		audioErro?.unload();
		audioErro = null;
	});

	/** Reage ao sinal reativo do composable e toca o áudio. */
	watch(
		() => f.deveTocarAudio.value,
		(deveTocar) => {
			if (deveTocar) {
				audioErro?.play();
				f.deveTocarAudio.value = false;
			}
		},
	);

	// ── Binding do peso como texto para o R3Input ────────────────────────────
	const pesoTexto = computed<string>({
		get: () => (f.pesoInformado.value > 0 ? String(f.pesoInformado.value) : ""),
		set: (v) => {
			const parsed = parseFloat(v.replace(",", "."));
			f.pesoInformado.value = isNaN(parsed) ? 0 : parsed;
		},
	});

	const pesoDecodificadoAutomaticamente = computed(
		() => f.pesoDecodificadoLocal.value !== null,
	);

	// ────────────────────────────────────────────────────────────────────────
	// Handlers
	// ────────────────────────────────────────────────────────────────────────

	async function onBipar() {
		await validarDun14(f.empresaId.value, f.mercadoriaId.value);
	}

	async function onCodigoBarrasInput() {
		const foiDecodificado = f.tentarDecodificarEanBalanca(f.codigoBipado.value);
		if (foiDecodificado) {
			await nextTick();
			(f.inputPesoRef.value as any)?.$el?.focus();
		}
	}

	async function onBiparPesoVariavel() {
		if (f.pesoDecodificadoLocal.value !== null) {
			await onValidarPeso();
		}
	}

	async function onValidarPeso() {
		await validarPesoVariavel(f.empresaId.value, f.mercadoriaId.value);
	}

	async function onFecharErroDialog() {
		f.fecharErroDialog();
		await nextTick();
		(f.inputDun14Ref.value as any)?.$el?.focus();
	}

	async function focarInputPrincipal() {
		await nextTick();
		(f.inputDun14Ref.value as any)?.$el?.focus();
	}
</script>
